import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { showAlert } from '../../../../../Assests/Utility';
import { getRemoteConfigSelector } from '../../../../../../redux/reducers/reducerSlices/RemoteConfigReducer';
import {
    getSproCarrierManagementSelector,
} from '../../../../../../redux/reducers/reducerSlices/SproCarrierManagementReducer';
import { sagaActions } from '../../../../../../sagas/sagaActions';
import DrilldownTableSS from '../../Dashboard/DefaultDashboard/DrillDownReports/Common/DrilldownTableSS';
import { CustomPaginationSS } from '../../../../Common/CustomPaginationSS/CustomPaginationSS';
import CommonToggleSwitch from '../../../../Common/CommonToggleSwitch/CommonToggleSwitch';
import SSPopup from '../../../../Common/Popup/SSPopup/SSPopup';
import { AppConstant } from '../../../../../Assests/AppConstant';
import WarehouseSearch from '../../../../SuperWarehouse/Components/SearchWarehouse/WarehouseSearch';
import CustomDropDown from '../../../../Common/CustomDropDown/CustomDropDown';

/**
 * SproCarrierStatusManagement
 * Displays and manages SPRO Carrier enable/disable status.
 * Columns: Carrier Name (Name), SCAC Code (SCAC), Date Deactivated (DateDeactivated),
 *          Status (isSPROEnabled), Actions (toggle)
 * Uses Redux saga for all API calls (no direct fetch).
 */
const SproCarrierStatusManagement = ({ themeSelected }) => {
    const dispatch = useDispatch();

    const remoteConfig = useSelector(getRemoteConfigSelector);
    const { allConfigration } = remoteConfig;
    const defaultLimit = Number(allConfigration?.PaginationDefaultLimit) || 50;

    const sproCarrierState = useSelector(getSproCarrierManagementSelector);
    const { sproCarrierList, operationObj, isLoading, totalElements } = sproCarrierState;

    const [currentPage, setCurrentPage] = useState(1);
    const [currentLimit, setCurrentLimit] = useState(defaultLimit);
    const [isFirst, setIsFirst] = useState(true);
    const [isLast, setIsLast] = useState(false);

    const [statusFilter, setStatusFilter] = useState('All');
    const [searchStr, setSearchStr] = useState('');

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedCarrier, setSelectedCarrier] = useState(null);
    const [pendingStatus, setPendingStatus] = useState(null);

    const tableTitleDD = [
        { id: 'Name', name: 'Carrier Name' },
        { id: 'SCAC', name: 'SCAC Code' },
        { id: 'UpdatedDate', name: 'Updated On' },
        { id: 'isSPROEnabled', name: 'Status' },
        { id: '', name: 'Actions' },
    ];

    // ── Fetch helper ─────────────────────────────────────────────────────────
    const fetchCarrierList = (pageNo = 1, limit = defaultLimit, statusType = 'All', search = null) => {
        const payload = {
            CRUDTYPE: 'S',
            CarrierId: null,
            StatusType: (statusType === 'All' || !statusType) ? null : statusType,
            isSPROEnabled: null,
            Search: search || null,
            UpdatedBy: null,
            UpdatedDate: null,
            PageNo: pageNo,
            Limit: limit,
        };
        dispatch({
            type: sagaActions.FETCH_SPRO_CARRIER_LIST,
            payload,
            operationObj: { type: 'Fetch Carriers' },
        });
    };

    // ── On mount ─────────────────────────────────────────────────────────────
    useEffect(() => {
        fetchCarrierList(1, defaultLimit, 'All', null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Debounced search ─────────────────────────────────────────────────────
    useEffect(() => {
        let timeout;
        if (searchStr.length > 2 || searchStr === '') {
            timeout = setTimeout(() => {
                setCurrentPage(1);
                fetchCarrierList(1, currentLimit, statusFilter, searchStr || null);
            }, 1000);
        }
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchStr]);

    // ── Pagination bookkeeping when new list arrives ──────────────────────────
    useEffect(() => {
        if (operationObj?.type === 'Fetch Carriers') {
            if (operationObj.status) {
                const totalPgs = Math.ceil(totalElements / currentLimit) || 1;
                setIsFirst(currentPage === 1);
                setIsLast(currentPage >= totalPgs || sproCarrierList.length < currentLimit);
            } else if (operationObj.status === false) {
                showAlert(toast.TYPE.ERROR, 'Unable to fetch carrier list', themeSelected);
            }
            dispatch({ type: sagaActions.RESET_SPRO_CARRIER_OPERATION_OBJ });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operationObj]);

    // ── Watch update response ────────────────────────────────────────────────
    useEffect(() => {
        if (operationObj?.type === 'Update Carrier Status') {
            if (operationObj.status) {
                showAlert(toast.TYPE.SUCCESS, operationObj.message || 'Updated Successfully', themeSelected);
                fetchCarrierList(currentPage, currentLimit, statusFilter, searchStr || null);
            } else if (operationObj.status === false) {
                showAlert(toast.TYPE.ERROR, operationObj.message || 'Unable to update carrier status', themeSelected);
            }
            dispatch({ type: sagaActions.RESET_SPRO_CARRIER_OPERATION_OBJ });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operationObj]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleToggleClick = (carrier) => {
        setSelectedCarrier(carrier);
        setPendingStatus(!carrier.isSPROEnabled);
        setShowConfirmModal(true);
    };

    const dispatchUpdateCarrierStatus = (carrier, newStatus) => {
        const payload = {
            CRUDTYPE: 'U',
            CarrierId: carrier.ID,
            StatusType: null,
            isSPROEnabled: newStatus,
            Search: null,
            UpdatedBy: 'SA',
            UpdatedDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
            PageNo: currentPage,
            Limit: currentLimit,
        };
        dispatch({
            type: sagaActions.UPDATE_SPRO_CARRIER_STATUS,
            payload,
            operationObj: { type: 'Update Carrier Status' },
        });
    };

    const popupBtnHandler = (text) => {
        if (text === AppConstant.commonStrings.yes) {
            dispatchUpdateCarrierStatus(selectedCarrier, pendingStatus);
        }
        setShowConfirmModal(false);
        setSelectedCarrier(null);
        setPendingStatus(null);
    };

    const pageChange = (pageNo, limit = defaultLimit) => {
        setCurrentPage(pageNo);
        setCurrentLimit(limit);
        fetchCarrierList(pageNo, limit, statusFilter, searchStr || null);
    };

    // ── Filter dropdown ───────────────────────────────────────────────────────
    const showCustomDropDownForFilters = () => {
        const filterDataArr = [
            { name: 'All', value: 'All' },
            { name: 'Active', value: 'Active' },
            { name: 'Inactive', value: 'Inactive' },
        ];
        return (
            <div className='d-flex align-items-center'>
                <CustomDropDown
                    themeSelected={themeSelected}
                    data={filterDataArr}
                    placeHolder={statusFilter || 'All'}
                    onSelect={(event) => {
                        setStatusFilter(event.value);
                        setCurrentPage(1);
                        fetchCarrierList(1, currentLimit, event.value, searchStr || null);
                    }}
                    keyStr={'name'}
                    title={'Filter by status'}
                    customClass={'min-width-12'}
                    key={statusFilter}
                />
            </div>
        );
    };

    // ── Table data formatter ──────────────────────────────────────────────────
    const formatListData = (data) =>
        data.map((el, index) => ({
            id: el.ID || index,
            0: el.Name || '-',
            1: el.SCAC || '-',
            2: el.UpdatedDate ? `${moment(el.UpdatedDate).format(AppConstant.commonStrings.MM_DD_YYYY_dateFormat)}\n${moment(el.UpdatedDate).format(AppConstant.commonStrings.HH_mm_dateFormat)}` : AppConstant.commonStrings.hypen,
            3: (
                <span className={`badge ${el.isSPROEnabled ? 'bg-success' : 'bg-danger'}`}>
                    {el.isSPROEnabled ? 'Enabled' : 'Disabled'}
                </span>
            ),
            4: (
                <div className='d-flex justify-content-center align-items-center'>
                    <CommonToggleSwitch
                        value={el.isSPROEnabled}
                        onChangeValue={() => handleToggleClick(el)}
                        allowPermissionD={true}
                    />
                </div>
            ),
        }));

    // ── Confirmation popup config ─────────────────────────────────────────────
    const confirmPopupObj = {
        id: 'sproCarrierStatusConfirm',
        modalLabel: 'sproCarrierStatusConfirm',
        showClose: true,
        showfooter: true,
        showHeader: true,
        sendDriverNotification: false,
        modalSize: 'md',
        title: 'Confirm Status Update',
        bodyText: `Are you sure you want to ${pendingStatus ? 'Enable' : 'Disable'} carrier "${selectedCarrier?.name || selectedCarrier?.Name}"?`,
        subText: '',
        keyboard: false,
        backdrop: 'static',
        footerBtn: {
            btn1: { show: true, text: AppConstant.commonStrings.yes },
            btn2: { show: true, text: AppConstant.commonStrings.no },
        },
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className=''>
            {/* ── Header row ──────────────────────────────────────────── */}
            <div className='d-flex justify-content-between align-items-center my-2 mx-3'>
                <div className='fw-bold'>SPRO Carrier Management</div>
                <div className='d-flex justify-content-end align-items-center gap-2'>
                    {/* Status filter */}
                    {showCustomDropDownForFilters()}
                    {/* Search */}
                    <div style={{ width: '220px' }}>
                        <WarehouseSearch
                            placeholder='Search carrier name...'
                            inputValue={searchStr}
                            cancelSearch={() => setSearchStr('')}
                            onChangeHandler={(text) => setSearchStr(text)}
                        />
                    </div>
                </div>
            </div>

            {/* ── Table ───────────────────────────────────────────────── */}
            <div className='mt-2 appointmentListTable'>
                <DrilldownTableSS
                    themeSelected={themeSelected}
                    bodyData={
                        sproCarrierList && sproCarrierList.length
                            ? formatListData(sproCarrierList)
                            : []
                    }
                    titleData={tableTitleDD}
                    showCollapse={false}
                    showInput={false}
                    showSort={false}
                    sortKey={false}
                    drillDownReport={sproCarrierList}
                    initiateDrillDownReport={isLoading}
                    loaderArray={5}
                    showUTCTime={false}
                    isPaginated={true}
                />
            </div>

            {/* ── Pagination ───────────────────────────────────────────── */}
            {sproCarrierList && sproCarrierList.length ? (
                <CustomPaginationSS
                    pageChange={(pageNo, limit) => pageChange(pageNo, limit)}
                    pageNo={currentPage}
                    pageSize={currentLimit}
                    totalElements={totalElements}
                    totalPages={Math.ceil(totalElements / currentLimit) || 1}
                    last={isLast}
                    first={isFirst}
                    data={sproCarrierList}
                    isLoading={isLoading}
                />
            ) : (
                ''
            )}

            {/* ── Confirmation Popup ───────────────────────────────────── */}
            {showConfirmModal && (
                <SSPopup
                    {...confirmPopupObj}
                    popupBtnHandler={(text) => popupBtnHandler(text)}
                    closepopup={() => {
                        setShowConfirmModal(false);
                        setSelectedCarrier(null);
                        setPendingStatus(null);
                    }}
                    themeSelected={themeSelected}
                    showModal={showConfirmModal}
                />
            )}
        </div>
    );
};

export default SproCarrierStatusManagement;